import type { Node as ProsemirrorNode } from 'prosemirror-model'

export const ZERO_WIDTH_SPACE = '\u200B'

export interface PlaceholderRange {
  start: number
  end: number
  key: string
}

/**
 * Builds the HTML for a single placeholder span plus its trailing zero-width
 * space. The ZWS gives the caret a landing spot just outside the placeholder,
 * and `contenteditable="false"` makes the span behave like an atomic chip.
 *
 * This is the single source of truth for placeholder markup — every place that
 * serializes a `{{key}}` token into HTML must go through here so the shape can
 * never drift between them.
 */
export function buildPlaceholderHtml(key: string, displayLabel: string): string {
  const labelAttr = displayLabel ? ` data-label="${displayLabel}"` : ''
  return `<span class="template-placeholder" data-key="${key}"${labelAttr} contenteditable="false">${displayLabel}</span>${ZERO_WIDTH_SPACE}`
}

/**
 * Walks the document and returns the position ranges covered by each placeholder
 * (including the trailing zero-width space that anchors the caret outside the
 * placeholder). Shared by the editor's keydown handling and the placeholder
 * protection plugin so they always agree on where a placeholder begins and ends.
 */
export function getPlaceholderRanges(doc: ProsemirrorNode): PlaceholderRange[] {
  const ranges: PlaceholderRange[] = []
  let current: PlaceholderRange | null = null
  doc.descendants((node, nodePos) => {
    if (!node.isText) return
    const mark = node.marks?.find((m) => m.type.name === 'placeholder')
    if (!mark) {
      current = null
      return
    }
    const key = String(mark.attrs?.key ?? mark.attrs?.label ?? 'placeholder')
    const start = nodePos
    const end = nodePos + node.nodeSize
    if (current && current.key === key && start <= current.end) {
      current.end = end
    } else {
      current = { start, end, key }
      ranges.push(current)
    }
  })
  ranges.forEach((range) => {
    // Guard the upper bound: textBetween throws if we read past the end of the doc.
    if (range.end >= doc.content.size) return
    const trailingChar = doc.textBetween(range.end, range.end + 1, '', '')
    if (trailingChar === ZERO_WIDTH_SPACE) {
      range.end += 1
    }
  })
  return ranges
}
